(() => {
  const formatMoney = (amount, currency) => {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(amount / 100);
    } catch (error) {
      return `\u00a3${(amount / 100).toFixed(2)}`;
    }
  };

  const initProductPage = (root) => {
    if (root.dataset.qePdpReady === 'true') return;

    const dataNode = root.querySelector('[data-qe-product-data]');
    if (!dataNode) return;

    let productData;
    try {
      productData = JSON.parse(dataNode.textContent);
    } catch (error) {
      console.error('Quality Eyes product data could not be read.', error);
      return;
    }

    root.dataset.qePdpReady = 'true';

    const variantSelect = root.querySelector('[data-qe-variant-select]');
    const variantInput = root.querySelector('.product-variant-id');
    const priceNode = root.querySelector('[data-qe-price]');
    const priceNote = root.querySelector('[data-qe-price-note]');
    const addButton = root.querySelector('[data-qe-add]');
    const addLabel = root.querySelector('[data-qe-add-label]');
    const stock = root.querySelector('[data-qe-stock]');
    const galleryMain = root.querySelector('[data-qe-gallery-main]');
    const galleryThumbs = [...root.querySelectorAll('[data-qe-gallery-thumb]')];
    const quantityInput = root.querySelector('[data-qe-quantity-input]');
    const quantityMinus = root.querySelector('[data-qe-quantity-minus]');
    const quantityPlus = root.querySelector('[data-qe-quantity-plus]');

    const state = {
      variant: productData.variants.find((variant) => String(variant.id) === variantSelect.value) || productData.variants[0],
    };

    const selectedPurchaseMode = () => {
      const selectedOption = root.querySelector('.sls-option:checked')
        || root.querySelector('.sls-option-container.sls-active .sls-option');
      return selectedOption?.dataset.type === 'subscription' ? 'subscription' : 'one_time';
    };

    const selectedSellingPlanId = () => {
      const selectedOption = root.querySelector('.sls-option:checked');
      const interval = selectedOption?.closest('.sls-option-container')?.querySelector('select[name="subs_interval"]');
      return interval?.value ? String(interval.value) : '';
    };

    const currentUnitPrice = () => {
      if (selectedPurchaseMode() !== 'subscription') return state.variant.price;

      const allocations = Array.isArray(state.variant.allocations) ? state.variant.allocations : [];
      if (!allocations.length) return state.variant.price;

      const planId = selectedSellingPlanId();
      const allocation = allocations.find((item) => String(item.id) === planId);
      return Number.isFinite(Number(allocation?.price)) ? Number(allocation.price) : state.variant.price;
    };

    const updatePrice = () => {
      const formattedPrice = formatMoney(currentUnitPrice(), productData.currency);
      if (priceNode.textContent !== formattedPrice) priceNode.textContent = formattedPrice;
      priceNote.textContent = state.variant.title;
      const comparePrice = root.querySelector('[data-qe-compare-price]');
      if (comparePrice) {
        comparePrice.hidden = !(Number(state.variant.compareAt) > Number(state.variant.price)
          && Number(state.variant.compareAt) > currentUnitPrice());
        const formattedComparePrice = formatMoney(state.variant.compareAt || 0, productData.currency);
        if (comparePrice.textContent !== formattedComparePrice) comparePrice.textContent = formattedComparePrice;
      }
    };

    const setGalleryImage = (src, alt, isPhoto = false) => {
      if (!src || !galleryMain) return;
      galleryMain.style.opacity = '0';
      window.setTimeout(() => {
        galleryMain.src = src;
        galleryMain.alt = alt || '';
        galleryMain.classList.toggle('is-photo', isPhoto);
        galleryMain.style.opacity = '1';
      }, 120);
    };

    const updateVariant = (variantId) => {
      const variant = productData.variants.find((item) => String(item.id) === String(variantId));
      if (!variant) return;

      state.variant = variant;
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      updatePrice();

      addButton.disabled = !variant.available;
      addLabel.textContent = variant.available ? 'Add to basket' : 'Sold out';
      stock.textContent = variant.available ? 'Available to order' : 'Currently unavailable';

      if (variant.image) {
        setGalleryImage(variant.image, variant.title, false);
        galleryThumbs.forEach((thumb) => {
          const active = thumb.dataset.image === variant.image;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-pressed', String(active));
        });
      }
    };

    galleryThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        galleryThumbs.forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        setGalleryImage(
          thumb.dataset.image,
          thumb.dataset.alt,
          thumb.classList.contains('qe-pdp-gallery__thumb--photo')
        );
      });
    });

    variantSelect.addEventListener('change', (event) => updateVariant(event.target.value));

    const normalizeQuantity = (value) => {
      if (!quantityInput) return 1;

      const min = Number.parseInt(quantityInput.min, 10) || 1;
      const step = Number.parseInt(quantityInput.step, 10) || 1;
      const maxValue = Number.parseInt(quantityInput.max, 10);
      const max = Number.isFinite(maxValue) ? maxValue : Number.POSITIVE_INFINITY;
      const parsed = Number.parseInt(value, 10);
      const requested = Number.isFinite(parsed) ? parsed : min;
      const stepped = min + Math.round((requested - min) / step) * step;
      const normalized = Math.min(max, Math.max(min, stepped));

      quantityInput.value = String(normalized);
      if (quantityMinus) quantityMinus.disabled = normalized <= min;
      if (quantityPlus) quantityPlus.disabled = Number.isFinite(max) && normalized >= max;
      return normalized;
    };

    if (quantityInput) {
      quantityMinus?.addEventListener('click', () => {
        const step = Number.parseInt(quantityInput.step, 10) || 1;
        normalizeQuantity((Number.parseInt(quantityInput.value, 10) || 1) - step);
      });
      quantityPlus?.addEventListener('click', () => {
        const step = Number.parseInt(quantityInput.step, 10) || 1;
        normalizeQuantity((Number.parseInt(quantityInput.value, 10) || 1) + step);
      });
      quantityInput.addEventListener('change', () => normalizeQuantity(quantityInput.value));
      quantityInput.addEventListener('blur', () => normalizeQuantity(quantityInput.value));
      normalizeQuantity(quantityInput.value);
    }

    const isPurchaseControl = (target) => target.matches(
      '.sls-option, select[name="subs_interval"], input[name="selling_plan"]'
    );

    root.addEventListener('change', (event) => {
      if (isPurchaseControl(event.target)) window.setTimeout(updatePrice, 0);
    });

    root.addEventListener('click', (event) => {
      if (!event.target.closest('.sls-option-container, [role="radio"]')) return;
      window.setTimeout(updatePrice, 0);
      window.setTimeout(updatePrice, 150);
    });

    let purchasePriceAttempts = 0;
    const purchasePriceTimer = window.setInterval(() => {
      purchasePriceAttempts += 1;
      updatePrice();
      if (purchasePriceAttempts >= 40) window.clearInterval(purchasePriceTimer);
    }, 250);

    const wishButton = root.querySelector('[data-qe-wish]');
    if (wishButton) {
      wishButton.addEventListener('click', () => {
        const selected = wishButton.getAttribute('aria-pressed') === 'true';
        wishButton.setAttribute('aria-pressed', String(!selected));
        wishButton.setAttribute('aria-label', selected ? 'Save for later' : 'Saved for later');
      });
    }

    const guide = root.querySelector('[data-qe-guide]');
    if (guide) {
      const tabs = [...guide.querySelectorAll('[data-qe-guide-tab]')];
      const panels = [...guide.querySelectorAll('[data-qe-guide-panel]')];

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const target = tab.dataset.qeGuideTab;
          tabs.forEach((item) => {
            const active = item === tab;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-selected', String(active));
          });
          panels.forEach((panel) => {
            const active = panel.dataset.qeGuidePanel === target;
            panel.classList.toggle('is-active', active);
            panel.hidden = !active;
          });
        });
      });
    }

    updatePrice();
  };

  const initAll = () => document.querySelectorAll('[data-qe-pdp]').forEach(initProductPage);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();

